'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import MetricCard from './MetricCard';
import ActivityItem from './ActivityItem';
import QuickActionCard from './QuickActionCard';
import IntegrationStatus from './IntegrationStatus';
import FavoritePortals from './FavoritePortals';

interface Metric {
  title: string;
  value: string | number;
  change: number;
  changeLabel: string;
  icon: string;
  trend: 'up' | 'down' | 'neutral';
  route?: string;
}

interface Activity {
  type: 'process' | 'task' | 'approval' | 'system';
  title: string;
  description: string;
  timestamp: string;
  user?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  iconColor?: string;
  route: string;
}

interface Integration {
  name: string;
  status: 'connected' | 'disconnected' | 'syncing' | 'error';
  lastSync?: string;
  icon: string;
}

interface Portal {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export default function DashboardInteractive() {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: Metric[] = [
    {
      title: 'Processos Ativos',
      value: 247,
      change: 12.5,
      changeLabel: 'vs. mês anterior',
      icon: 'RectangleStackIcon',
      trend: 'up',
      route: '/process-management',
    },
    {
      title: 'Tarefas Pendentes',
      value: 89,
      change: -8.3,
      changeLabel: 'vs. semana anterior',
      icon: 'ClipboardDocumentCheckIcon',
      trend: 'down',
      route: '/task-management-center',
    },
    {
      title: 'Taxa de Conclusão',
      value: '94.2%',
      change: 3.1,
      changeLabel: 'vs. mês anterior',
      icon: 'ChartBarIcon',
      trend: 'up',
      route: '/analytics-dashboard',
    },
    {
      title: 'Violações de SLA',
      value: 12,
      change: -15.7,
      changeLabel: 'vs. mês anterior',
      icon: 'ExclamationTriangleIcon',
      trend: 'down',
      route: '/analytics-dashboard',
    },
  ];

  const activities: Activity[] = [
    {
      type: 'process',
      title: 'Novo Processo Iniciado',
      description: 'Aprovação de Orçamento Q4 2024 foi iniciado',
      timestamp: '5 min atrás',
      user: 'Maria Silva',
      status: 'info',
    },
    {
      type: 'task',
      title: 'Tarefa Concluída',
      description: 'Revisão de Relatório Trimestral foi concluída',
      timestamp: '15 min atrás',
      user: 'João Santos',
      status: 'success',
    },
    {
      type: 'approval',
      title: 'Aprovação Necessária',
      description: 'Solicitação de Compra #1247 aguarda sua aprovação',
      timestamp: '1 hora atrás',
      user: 'Ana Costa',
      status: 'warning',
    },
    {
      type: 'system',
      title: 'Sincronização Concluída',
      description: 'Dados do ERP foram sincronizados com sucesso',
      timestamp: '2 horas atrás',
      status: 'success',
    },
    {
      type: 'task',
      title: 'Tarefa Atribuída',
      description: 'Validação de Dados foi atribuída a você',
      timestamp: '3 horas atrás',
      user: 'Carlos Oliveira',
      status: 'info',
    },
    {
      type: 'process',
      title: 'Processo Concluído',
      description: 'Onboarding de Funcionário #2024-089 foi finalizado',
      timestamp: '4 horas atrás',
      user: 'Paula Ferreira',
      status: 'success',
    },
  ];

  const quickActions: QuickAction[] = [
    {
      title: 'Criar Novo Processo',
      description: 'Inicie um novo processo usando o assistente guiado',
      icon: 'PlusCircleIcon',
      iconColor: 'text-primary',
      route: '/process-creation-wizard',
    },
    {
      title: 'Galeria de Templates',
      description: 'Explore templates prontos para uso da comunidade',
      icon: 'RectangleGroupIcon',
      iconColor: 'text-accent',
      route: '/template-gallery',
    },
    {
      title: 'Construtor de Formulários',
      description: 'Crie formulários dinâmicos sem código',
      icon: 'DocumentTextIcon',
      iconColor: 'text-success',
      route: '/dynamic-form-builder',
    },
    {
      title: 'Minhas Tarefas',
      description: 'Visualize e gerencie suas tarefas atribuídas',
      icon: 'ClipboardDocumentListIcon',
      iconColor: 'text-warning',
      route: '/task-management-center',
    },
    {
      title: 'Construtor de Portal',
      description: 'Personalize portais com widgets arrastar e soltar',
      icon: 'PaintBrushIcon',
      iconColor: 'text-primary',
      route: '/portal-builder',
    },
    {
      title: 'Análises',
      description: 'Visualize métricas e KPIs de desempenho',
      icon: 'ChartPieIcon',
      iconColor: 'text-accent',
      route: '/analytics-dashboard',
    },
  ];

  const integrations: Integration[] = [
    {
      name: 'SAP ERP',
      status: 'connected',
      lastSync: '08/12/2024 18:15',
      icon: 'ServerIcon',
    },
    {
      name: 'Salesforce CRM',
      status: 'connected',
      lastSync: '08/12/2024 18:10',
      icon: 'UserGroupIcon',
    },
    {
      name: 'Active Directory',
      status: 'syncing',
      lastSync: '08/12/2024 18:20',
      icon: 'ShieldCheckIcon',
    },
    {
      name: 'Oracle Database',
      status: 'connected',
      lastSync: '08/12/2024 18:05',
      icon: 'CircleStackIcon',
    },
  ];

  const favoritePortals: Portal[] = [
    {
      id: 'portal-1',
      name: 'Portal Executivo',
      description: 'Visão geral de KPIs e métricas estratégicas',
      icon: 'ChartBarIcon',
      color: 'bg-primary',
    },
    {
      id: 'portal-2',
      name: 'Portal de Aprovações',
      description: 'Gerencie todas as aprovações pendentes',
      icon: 'CheckCircleIcon',
      color: 'bg-success',
    },
    {
      id: 'portal-3',
      name: 'Portal de RH',
      description: 'Processos de recursos humanos e onboarding',
      icon: 'UsersIcon',
      color: 'bg-accent',
    },
    {
      id: 'portal-4',
      name: 'Portal Financeiro',
      description: 'Processos financeiros e orçamentários',
      icon: 'CurrencyDollarIcon',
      color: 'bg-warning',
    },
  ];

  const handleMetricClick = (route?: string) => {
    if (route && isHydrated) {
      router.push(route);
    }
  };

  const handleQuickActionClick = (route: string) => {
    if (isHydrated) {
      router.push(route);
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:267:6" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="267" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:268:8" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="268" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%20p-6%22%7D" className="animate-pulse space-y-6 p-6">
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:269:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="269" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:271:14" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="271" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-lg%22%7D" key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:274:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="274" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:275:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="275" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20h-96%20bg-muted%20rounded-lg%22%7D" className="lg:col-span-2 h-96 bg-muted rounded-lg"></div>
            <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:276:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="276" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:284:4" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="284" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:285:6" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="285" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <MetricCard data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:287:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="287" data-component-file="DashboardInteractive.tsx" data-component-name="MetricCard" data-component-content="%7B%22elementName%22%3A%22MetricCard%22%7D"
            key={index}
            title={metric.title}
            value={metric.value}
            change={metric.change}
            changeLabel={metric.changeLabel}
            icon={metric.icon}
            trend={metric.trend}
            onClick={metric.route ? () => handleMetricClick(metric.route) : undefined}
          />
        ))}
      </div>

      <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:300:6" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="300" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:301:8" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="301" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-6%22%7D" className="lg:col-span-2 space-y-6">
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:302:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="302" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-6%22%7D" className="bg-card border border-border rounded-lg p-6">
            <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:303:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="303" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%22%7D" className="flex items-center justify-between mb-4">
              <h2 data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:304:14" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="304" data-component-file="DashboardInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Atividade%20Recente%22%7D" className="text-xl font-semibold text-foreground">Atividade Recente</h2>
              <button data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:305:14" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="305" data-component-file="DashboardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-sm%20text-accent%20hover%3Atext-accent%2F80%20transition-micro%22%2C%22textContent%22%3A%22Ver%20todas%22%7D" className="text-sm text-accent hover:text-accent/80 transition-micro">
                Ver todas
              </button>
            </div>
            <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:309:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="309" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%20max-h-%5B600px%5D%20overflow-y-auto%22%7D" className="space-y-2 max-h-[600px] overflow-y-auto">
              {activities.map((activity, index) => (
                <ActivityItem data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:311:16" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="311" data-component-file="DashboardInteractive.tsx" data-component-name="ActivityItem" data-component-content="%7B%22elementName%22%3A%22ActivityItem%22%7D"
                  key={index}
                  type={activity.type}
                  title={activity.title}
                  description={activity.description}
                  timestamp={activity.timestamp}
                  user={activity.user}
                  status={activity.status}
                />
              ))}
            </div>
          </div>

          <IntegrationStatus data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:324:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="324" data-component-file="DashboardInteractive.tsx" data-component-name="IntegrationStatus" data-component-content="%7B%22elementName%22%3A%22IntegrationStatus%22%7D" integrations={integrations} />
        </div>

        <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:327:8" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="327" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:328:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="328" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-6%22%7D" className="bg-card border border-border rounded-lg p-6">
            <h2 data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:329:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="329" data-component-file="DashboardInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22A%C3%A7%C3%B5es%20R%C3%A1pidas%22%7D" className="text-xl font-semibold text-foreground mb-4">Ações Rápidas</h2>
            <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:330:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="330" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
              {quickActions.map((action, index) => (
                <QuickActionCard data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:332:16" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="332" data-component-file="DashboardInteractive.tsx" data-component-name="QuickActionCard" data-component-content="%7B%22elementName%22%3A%22QuickActionCard%22%7D"
                  key={index}
                  title={action.title}
                  description={action.description}
                  icon={action.icon}
                  iconColor={action.iconColor}
                  onClick={() => handleQuickActionClick(action.route)}
                />
              ))}
            </div>
          </div>

          <FavoritePortals data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:344:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="344" data-component-file="DashboardInteractive.tsx" data-component-name="FavoritePortals" data-component-content="%7B%22elementName%22%3A%22FavoritePortals%22%7D" portals={favoritePortals} />
        </div>
      </div>

      <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:348:6" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="348" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-6%22%7D" className="bg-card border border-border rounded-lg p-6">
        <h2 data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:349:8" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="349" data-component-file="DashboardInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Atalhos%20de%20Teclado%22%7D" className="text-xl font-semibold text-foreground mb-4">Atalhos de Teclado</h2>
        <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:350:8" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="350" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:351:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="351" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20bg-muted%2F50%20rounded-lg%22%7D" className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <kbd data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:352:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="352" data-component-file="DashboardInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-3%20py-1%20bg-background%20border%20border-border%20rounded%20text-sm%20font-mono%22%2C%22textContent%22%3A%22Alt%20%2B%20N%22%7D" className="px-3 py-1 bg-background border border-border rounded text-sm font-mono">
              Alt + N
            </kbd>
            <span data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:355:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="355" data-component-file="DashboardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Novo%20Processo%22%7D" className="text-sm text-muted-foreground">Novo Processo</span>
          </div>
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:357:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="357" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20bg-muted%2F50%20rounded-lg%22%7D" className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <kbd data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:358:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="358" data-component-file="DashboardInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-3%20py-1%20bg-background%20border%20border-border%20rounded%20text-sm%20font-mono%22%2C%22textContent%22%3A%22Alt%20%2B%20T%22%7D" className="px-3 py-1 bg-background border border-border rounded text-sm font-mono">
              Alt + T
            </kbd>
            <span data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:361:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="361" data-component-file="DashboardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Minhas%20Tarefas%22%7D" className="text-sm text-muted-foreground">Minhas Tarefas</span>
          </div>
          <div data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:363:10" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="363" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20bg-muted%2F50%20rounded-lg%22%7D" className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <kbd data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:364:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="364" data-component-file="DashboardInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-3%20py-1%20bg-background%20border%20border-border%20rounded%20text-sm%20font-mono%22%2C%22textContent%22%3A%22Alt%20%2B%20S%22%7D" className="px-3 py-1 bg-background border border-border rounded text-sm font-mono">
              Alt + S
            </kbd>
            <span data-component-id="src/app/main-dashboard/components/DashboardInteractive.tsx:367:12" data-component-path="src/app/main-dashboard/components/DashboardInteractive.tsx" data-component-line="367" data-component-file="DashboardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Busca%20Global%22%7D" className="text-sm text-muted-foreground">Busca Global</span>
          </div>
        </div>
      </div>
    </div>
  );
}